import React, { useEffect, useState, useRef } from "react";
import {
  useGetGiftsQuery,
  useUpdateGiftMutation,
  useAddGiftMutation,
  useGetAllGiftsQuery,
  useUpdateGiftQuantityMutation,
  useUploadGiftImageMutation,
} from "../redux/crmApi";

const GiftPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentAllGiftsPage, setCurrentAllGiftsPage] = useState(1);
  const [mainTab, setMainTab] = useState("pending");
  const [selectedScore, setSelectedScore] = useState(100);
  const [selectedSize, setSelectedSize] = useState("");
  const [enteredQuantity, setEnteredQuantity] = useState("");

  const fileInputRef = useRef(null);
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString([], {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
    const timePart = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${datePart}\n${timePart}`;
  };

  const [newGift, setNewGift] = useState({
    gift_name: "",
    image: null,
    customizable: "",
    sizes: [], // Array to store sizes and their quantities
    quantity: "", // General quantity (if no size is selected)
  });
  const [editingGift, setEditingGift] = useState(null);
  const giftsPerPage = 10;
  const {
    data: allGiftsResponse,
    isLoading: isLoadingAllGifts,
    error: allGiftsError,
  } = useGetAllGiftsQuery();
  const [allGifts, setAllGifts] = useState([]);
  useEffect(() => {
    if (allGiftsResponse) {
      setAllGifts(allGiftsResponse);
    }
  }, [allGiftsResponse]);
  const [editGiftId, setEditGiftId] = useState(null);
  const [editQuantity, setEditQuantity] = useState("");
  const [updateGiftQuantity] = useUpdateGiftQuantityMutation();
  const [uploadGiftImage] = useUploadGiftImageMutation();
  // const handleUpdateGift = async () => {
  //   if (!editingGift) return;

  //   try {
  //     const { purchased_gift_id, status, score, gift_date } = editingGift;

  //     await updateGift({
  //       id: purchased_gift_id, // API expects gift ID
  //       status,
  //       score,
  //       gift_date
  //     }).unwrap(); // Ensures promise resolution

  //     setEditingGift(null); // Reset editing state
  //   } catch (error) {
  //     console.error("Error updating gift:", error);
  //   }
  // };

  const handleUpdateQuantity = async (stockId, newQuantity) => {
    try {
      await updateGiftQuantity({
        stock_id: stockId,
        quantity: newQuantity,
      }).unwrap();
      // This should trigger a re-fetch if your API is properly configured with tags
      setEditGiftId(null);
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const handleUpdateGift = async () => {
    if (!editingGift) return;
    try {
      await updateGift({
        id: editingGift.player_gift_status_id,
        gift_status: editingGift.gift_status,
      }).unwrap();
      setEditingGift(null);
    } catch (error) {
      console.error("Error updating gift:", error);
    }
  };
  const handleEdit = (giftId, quantity) => {
    setEditGiftId(giftId);
    setEditQuantity(quantity);
  };

  const { data: giftsResponse, isLoading, error } = useGetGiftsQuery();
  const [updateGift] = useUpdateGiftMutation();
  const [addGift] = useAddGiftMutation();

  const gifts = React.useMemo(() => {
    if (!giftsResponse) return { pending: [], delivered: [] };

    return {
      pending: giftsResponse.other_statuses || [], // Remove the filter to show all statuses
      delivered: giftsResponse.delivered || [],
    };
  }, [giftsResponse]);

  if (isLoading) return <p>Loading gifts...</p>;
  if (error) return <p>Error fetching gifts</p>;

  if (mainTab === "addNew" && isLoadingAllGifts)
    return <p>Loading all gifts...</p>;
  if (mainTab === "addNew" && allGiftsError)
    return <p>Error fetching all gifts</p>;

  const filterGiftsByName = (giftsList) => {
    return giftsList.filter((gift) =>
      gift.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };
  const filterAllGiftsByName = (gifts) => {
    if (!searchTerm) return [...gifts]; // Always return new array
    return gifts.filter((gift) =>
      gift.gift_name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };
  const filterGiftsByScore = (giftsList) => {
    return giftsList.filter(
      (gift) => Number(gift.score) === Number(selectedScore),
    );
  };

  const sortGifts = (giftsList) => {
    return [...giftsList].sort((a, b) => {
      let valA = a[sortBy];
      let valB = b[sortBy];

      if (sortBy === "score") {
        valA = a.score;
        valB = b.score;
      }

      if (typeof valA === "string")
        return sortOrder === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      return sortOrder === "asc" ? valA - valB : valB - valA;
    });
  };

  const getPaginatedGifts = (giftsList) => {
    const indexOfLastGift = currentPage * giftsPerPage;
    const indexOfFirstGift = indexOfLastGift - giftsPerPage;
    return giftsList.slice(indexOfFirstGift, indexOfLastGift);
  };

  const getPaginatedAllGifts = (giftsList, currentPage, itemsPerPage) => {
    if (!Array.isArray(giftsList) || giftsList.length === 0) return [];

    // Debug: Verify input
    console.log("Total gifts before pagination:", giftsList.length);
    console.log(
      "Unique stock_ids:",
      new Set(giftsList.map((g) => g.stock_id)).size,
    );

    const indexOfLastGift = currentPage * itemsPerPage;
    const indexOfFirstGift = Math.max(0, indexOfLastGift - itemsPerPage);
    const paginated = giftsList.slice(indexOfFirstGift, indexOfLastGift);

    // Debug: Verify output
    console.log(
      `Paginated (${indexOfFirstGift}-${indexOfLastGift}):`,
      paginated,
    );
    return paginated;
  };

  // Process data in clear steps:
  const getAllGiftsData = () => {
    // 1. Filter first
    const filtered = filterAllGiftsByName(allGifts);
    console.log("After name filter:", filtered);

    // 2. Then sort
    const sorted = filtered.sort((a, b) =>
      a.gift_name.localeCompare(b.gift_name),
    );
    console.log("After sorting:", sorted);

    // 3. Finally paginate
    return getPaginatedAllGifts(sorted, currentAllGiftsPage, giftsPerPage);
  };

  const currentGifts = (() => {
    try {
      if (mainTab === "addNew") {
        return getAllGiftsData();
      } else {
        // Safely get the tab's gifts or default to empty array
        const tabGifts = Array.isArray(gifts[mainTab]) ? gifts[mainTab] : [];

        // Apply filters, sort, then paginate
        const filteredByName = filterGiftsByName(tabGifts);
        const filteredByScore = filterGiftsByScore(filteredByName);
        const sorted = sortGifts(filteredByScore);

        return getPaginatedGifts(sorted);
      }
    } catch (error) {
      console.error("Error processing gifts:", error);
      return []; // Fallback to empty array
    }
  })();

  console.log("Final currentGifts:", currentGifts);

  const handleAddSize = () => {
    if (!enteredQuantity) {
      alert("Please enter a quantity.");
      return;
    }

    if (!selectedSize) {
      alert("Please select a size.");
      return;
    }

    setNewGift((prevState) => ({
      ...prevState,
      sizes: [
        ...prevState.sizes,
        { size: selectedSize, quantity: parseInt(enteredQuantity) },
      ],
    }));

    // Reset the input fields
    setSelectedSize("");
    setEnteredQuantity("");
  };

  const handleAddGift = async () => {
    if (!newGift.image || (newGift.sizes.length === 0 && !newGift.quantity)) {
      alert(
        "Please select an image and add at least one size with quantity or a general quantity.",
      );
      return;
    }

    try {
      // 🔹 1. Upload Image to S3 (via API)
      const formData = new FormData();
      formData.append("file", newGift.image); // Attach image

      const uploadResponse = await uploadGiftImage(formData).unwrap();
      const imageUrl = uploadResponse.url; // Get the S3 URL

      // 🔹 2. Save Gift Data with Image URL, Sizes, and General Quantity
      const newGiftData = {
        gift_name: newGift.gift_name,
        image_url: imageUrl, // Store the S3 URL
        customizable: newGift.customizable,
        sizes: newGift.sizes.length > 0 ? newGift.sizes : null, // Include sizes if available
        quantity: newGift.quantity || null, // Include general quantity if available
      };

      await addGift(newGiftData).unwrap();

      // 🔹 3. Clear Form Fields
      setNewGift({
        gift_name: "",
        image: null,
        customizable: "",
        sizes: [],
        quantity: "",
      });

      // 🔹 4. Clear the file input field
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Clear the file input
      }

      alert("Gift added successfully!");
    } catch (error) {
      console.error("Error uploading gift:", error);
      alert("Failed to upload gift.");
    }
  };

  // const handleUpdateGift = async () => {
  //   await updateGift(editingGift).unwrap();
  //   setEditingGift(null);
  // };
  // const handleUpdateGift = async () => {
  //   if (!editingGift) return;
  //   console.log("editingGift", editingGift)

  //   try {
  //     await updateGift({
  //       id: editingGift.player_gift_status_id, // Ensure correct ID is passed
  //       gift_status: editingGift.gift_status
  //     }).unwrap();

  //     setEditingGift(null); // Reset editing state after success
  //   } catch (error) {
  //     console.error("Error updating gift:", error);
  //   }
  // };

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <button
          onClick={() => setMainTab("pending")}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: mainTab === "pending" ? "#50868f" : "#ddd",
            color: mainTab === "pending" ? "#fff" : "#000",
            fontSize: "16px",
            cursor: "pointer",
            transition: "background-color 0.3s ease",
          }}
        >
          Gift Pending
        </button>
        <button
          onClick={() => setMainTab("delivered")}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: mainTab === "delivered" ? "#50868f" : "#ddd",
            color: mainTab === "delivered" ? "#fff" : "#000",
            fontSize: "16px",
            cursor: "pointer",
            transition: "background-color 0.3s ease",
          }}
        >
          Gift Delivered
        </button>
        <button
          onClick={() => setMainTab("addNew")}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: mainTab === "addNew" ? "#50868f" : "#ddd",
            color: mainTab === "addNew" ? "#fff" : "#000",
            fontSize: "16px",
            cursor: "pointer",
            transition: "background-color 0.3s ease",
          }}
        >
          Add New Gift
        </button>
      </div>

      {mainTab === "addNew" ? (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <input
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  width: "300px",
                  fontSize: "16px",
                  outline: "none",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <label style={{ fontSize: "16px", fontWeight: "500" }}>
                Sort by:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  fontSize: "16px",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                <option value="name">Name</option>
                {/* <option value="achieved_score">Achieved Score</option>
                <option value="gift_date">Gift Date</option> */}
              </select>

              <button
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#50868f",
                  color: "#fff",
                  fontSize: "16px",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                  ":hover": {
                    backgroundColor: "#3a6a73",
                  },
                }}
              >
                {sortOrder === "asc" ? "⬆️ Ascending" : "⬇️ Descending"}
              </button>
            </div>
          </div>
          <h2>All Gifts</h2>
          <div
            style={{
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                backgroundColor: "#fff",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Stock ID
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Image
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Customizable
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Size
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Quantity
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentGifts.map((gift) => {
                  // Debugging: Log each gift's stock_id to verify uniqueness
                  console.log(`Rendering gift with stock_id: ${gift.stock_id}`);

                  return (
                    <tr
                      key={gift.stock_id}
                      style={{
                        borderBottom: "1px solid #ddd",
                        transition: "background-color 0.3s ease",
                      }}
                    >
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {gift.stock_id}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {gift.gift_name}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        <img
                          src={gift.image_url}
                          alt={gift.gift_name}
                          width="50"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "fallback-image-url";
                          }}
                        />
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {gift.customizable ? "Yes" : "No"}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {gift.size}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {editGiftId === gift.stock_id ? (
                          <input
                            type="number"
                            value={editQuantity}
                            onChange={(e) =>
                              setEditQuantity(
                                Math.max(0, parseInt(e.target.value) || 0),
                              )
                            }
                            style={{ padding: "8px", width: "60px" }}
                            min="0"
                          />
                        ) : (
                          gift.quantity
                        )}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {editGiftId === gift.stock_id ? (
                          <>
                            <button
                              onClick={() =>
                                handleUpdateQuantity(
                                  gift.stock_id,
                                  editQuantity,
                                )
                              }
                              style={{
                                padding: "8px 12px",
                                borderRadius: "8px",
                                border: "none",
                                backgroundColor: "#4CAF50",
                                color: "#fff",
                                cursor: "pointer",
                                marginRight: "8px",
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditGiftId(null)}
                              style={{
                                padding: "8px 12px",
                                borderRadius: "8px",
                                border: "none",
                                backgroundColor: "#f44336",
                                color: "#fff",
                                cursor: "pointer",
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() =>
                              handleEdit(gift.stock_id, gift.quantity)
                            }
                            style={{
                              padding: "8px 12px",
                              borderRadius: "8px",
                              border: "none",
                              backgroundColor: "#50868f",
                              color: "#fff",
                              cursor: "pointer",
                            }}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "20px",
                  gap: "10px",
                }}
              >
                <button
                  onClick={() =>
                    setCurrentAllGiftsPage(currentAllGiftsPage - 1)
                  }
                  disabled={currentAllGiftsPage === 1}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#50868f",
                    color: "#fff",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                >
                  ◀️ Prev
                </button>
                <span style={{ fontSize: "16px", fontWeight: "500" }}>
                  Page {currentAllGiftsPage}
                </span>
                <button
                  onClick={() =>
                    setCurrentAllGiftsPage(currentAllGiftsPage + 1)
                  }
                  disabled={currentGifts.length < giftsPerPage}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#50868f",
                    color: "#fff",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                >
                  Next ▶️
                </button>
              </div>
            </table>
          </div>

          <h2
            style={{
              marginTop: "20px",
              color: "#50868f",
              fontSize: "24px",
              fontWeight: "600",
            }}
          >
            Add New Gift
          </h2>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              marginTop: "10px",
            }}
          >
            {/* Gift Name */}
            <input
              type="text"
              placeholder="Gift Name"
              value={newGift.gift_name}
              onChange={(e) =>
                setNewGift({ ...newGift, gift_name: e.target.value })
              }
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "16px",
                outline: "none",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                transition: "border-color 0.3s ease",
                ":focus": {
                  borderColor: "#50868f",
                },
              }}
            />

            {/* Upload Image */}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setNewGift({ ...newGift, image: file });
                }
              }}
              ref={fileInputRef}
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "16px",
                outline: "none",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                transition: "border-color 0.3s ease",
                ":focus": {
                  borderColor: "#50868f",
                },
              }}
            />

            {/* Customizable Dropdown (Yes/No) */}
            <select
              value={newGift.customizable}
              onChange={(e) =>
                setNewGift({ ...newGift, customizable: e.target.value })
              }
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "16px",
                outline: "none",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                transition: "border-color 0.3s ease",
                ":focus": {
                  borderColor: "#50868f",
                },
              }}
            >
              <option value="">Select Customizable</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>

            {/* Size and Quantity Input */}
            <div style={{ display: "flex", gap: "10px" }}>
              <select
                value={selectedSize}
                onChange={(e) => setSelectedSize(e.target.value)}
                id="size"
                style={{
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  fontSize: "16px",
                  outline: "none",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  transition: "border-color 0.3s ease",
                  ":focus": {
                    borderColor: "#50868f",
                  },
                }}
              >
                <option value="">Select Size</option>
                <option value="S">Small</option>
                <option value="M">Medium</option>
                <option value="L">Large</option>
              </select>
              <input
                id="quantity"
                type="number"
                placeholder="Quantity"
                style={{
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  fontSize: "16px",
                  outline: "none",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  transition: "border-color 0.3s ease",
                  ":focus": {
                    borderColor: "#50868f",
                  },
                }}
              />
              <button
                onClick={handleAddSize}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#50868f",
                  color: "#fff",
                  fontSize: "16px",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                  ":hover": {
                    backgroundColor: "#3a6a73",
                  },
                }}
              >
                Add Size/Quantity
              </button>
            </div>

            {/* Display Selected Sizes and Quantities */}
            <div>
              <h4>Selected Sizes and Quantities:</h4>
              <ul>
                {newGift.sizes.map((sizeObj, index) => (
                  <li key={index}>
                    {sizeObj.size}: {sizeObj.quantity}
                  </li>
                ))}
              </ul>
              {newGift.quantity && <p>General Quantity: {newGift.quantity}</p>}
            </div>

            {/* Add Gift Button */}
            <button
              onClick={handleAddGift}
              style={{
                backgroundColor: "#50868f",
                color: "#fff",
                padding: "10px 20px",
                borderRadius: "8px",
                border: "none",
                fontSize: "16px",
                cursor: "pointer",
                transition: "background-color 0.3s ease",
                ":hover": {
                  backgroundColor: "#3a6a73",
                },
              }}
            >
              Add Gift
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: "10px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    width: "300px",
                    fontSize: "16px",
                    outline: "none",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                />
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <label style={{ fontSize: "16px", fontWeight: "500" }}>
                  Sort by:
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    padding: "8px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "16px",
                    outline: "none",
                    cursor: "pointer",
                  }}
                >
                  <option value="name">Name</option>
                  <option value="achieved_score">Achieved Score</option>
                  <option value="gift_date">Gift Date</option>
                </select>

                <button
                  onClick={() =>
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                  }
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#50868f",
                    color: "#fff",
                    fontSize: "16px",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                    ":hover": {
                      backgroundColor: "#3a6a73",
                    },
                  }}
                >
                  {sortOrder === "asc" ? "⬆️ Ascending" : "⬇️ Descending"}
                </button>
              </div>
            </div>
            <label
              style={{
                fontSize: "16px",
                fontWeight: "500",
                marginRight: "10px",
              }}
            >
              Select Score:
            </label>
            <select
              value={selectedScore}
              onChange={(e) => setSelectedScore(Number(e.target.value))}
              style={{
                padding: "8px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "16px",
                outline: "none",
                cursor: "pointer",
              }}
            >
              <option value={100}>100 Points</option>
              <option value={300}>300 Points</option>
              <option value={600}>600 Points</option>
              <option value={1000}>1000 Points</option>
            </select>
          </div>

          <div
            style={{
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                backgroundColor: "#fff",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Player ID
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Country Code
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Phone
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Gift Selected
                  </th>
                  {/* <th style={{ backgroundColor: "#50868f", color: "#fff", padding: "16px", textAlign: "left" }}>Achieved Score</th> */}
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Gift Date
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Status
                  </th>
                  {mainTab === "pending" && (
                    <th
                      style={{
                        backgroundColor: "#50868f",
                        color: "#fff",
                        padding: "16px",
                        textAlign: "left",
                      }}
                    >
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentGifts.map((gift) => (
                  <tr key={gift.player_id} style={{ borderBottom: "1px solid #ddd" }}>
                    <td style={{ padding: "16px", textAlign: "left" }}>{gift.player_id}</td>
                    <td style={{ padding: "16px", textAlign: "left" }}>{gift.name}</td>
                    <td style={{ padding: "16px", textAlign: "left" }}>{gift.country_code}</td>
                    <td style={{ padding: "16px", textAlign: "left" }}>{gift.phone}</td>
                    <td style={{ padding: "16px", textAlign: "left" }}>{gift.purchased_gift} {gift.size ? ` (${gift.size})` : ""}</td>
                    {/* <td style={{ padding: "16px", textAlign: "left" }}>{gift.score}</td> */}
                    <td
                      style={{
                        padding: "16px",
                        textAlign: "left",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {formatTime(gift.gift_date)}
                    </td>
                    <td style={{ padding: "16px", textAlign: "left" }}>
                      {mainTab === "pending" &&
                      editingGift?.player_gift_status_id ===
                        gift.player_gift_status_id ? (
                        <select
                          value={editingGift.gift_status}
                          onChange={(e) =>
                            setEditingGift({
                              ...editingGift,
                              gift_status: e.target.value,
                            })
                          }
                          style={{
                            padding: "8px",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                            backgroundColor: "#f8f8f8",
                          }}
                        >
                          <option value="pending">Printing</option>
                          <option value="shipped">Packing</option>
                          <option value="delivered">Delivered</option>
                        </select>
                      ) : (
                        <span
                          style={{
                            color:
                              gift.gift_status === "pending"
                                ? "#FFA500"
                                : gift.gift_status === "designing"
                                  ? "#4169E1"
                                  : gift.gift_status === "production"
                                    ? "#9370DB"
                                    : gift.gift_status === "shipped"
                                      ? "#32CD32"
                                      : "#008000",
                            fontWeight: "bold",
                            padding: "8px 12px",
                            borderRadius: "4px",
                            backgroundColor: "#f8f8f8",
                            cursor:
                              mainTab === "pending" ? "pointer" : "default",
                          }}
                          onClick={() =>
                            mainTab === "pending" && setEditingGift(gift)
                          }
                        >
                          {gift.gift_status}
                        </span>
                      )}
                    </td>
                    {mainTab === "pending" && (
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {editingGift?.player_gift_status_id ===
                        gift.player_gift_status_id ? (
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              onClick={handleUpdateGift}
                              style={{
                                padding: "8px 12px",
                                borderRadius: "4px",
                                border: "none",
                                backgroundColor: "#4CAF50",
                                color: "#fff",
                                cursor: "pointer",
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingGift(null)}
                              style={{
                                padding: "8px 12px",
                                borderRadius: "4px",
                                border: "none",
                                backgroundColor: "#f44336",
                                color: "#fff",
                                cursor: "pointer",
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditingGift(gift)}
                            style={{
                              padding: "8px 16px",
                              borderRadius: "8px",
                              border: "none",
                              backgroundColor: "#50868f",
                              color: "#fff",
                              cursor: "pointer",
                            }}
                          >
                            Update
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* {editingGift && (
  <div style={{ marginTop: "20px", padding: "20px", backgroundColor: "#f0f0f0", borderRadius: "8px" }}>
    <h3>Update Gift Status</h3>
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <select
        value={editingGift.status}
        onChange={(e) => setEditingGift({ ...editingGift, status: e.target.value })}
        style={{
          padding: "10px",
          borderRadius: "8px",
          border: "1px solid #ddd",
          fontSize: "16px"
        }}
      >
        <option value="pending">Pending</option>
        <option value="designing">Designing</option>
        <option value="production">Production</option>
        <option value="shipped">Shipped</option>
        <option value="delivered">Delivered</option>
      </select>
      
      <button
        onClick={handleUpdateGift}
        style={{
          padding: "10px",
          borderRadius: "8px",
          border: "none",
          backgroundColor: "#50868f",
          color: "#fff",
          cursor: "pointer"
        }}
      >
        Update Status
      </button>
    </div>
  </div>
)} */}

          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#50868f",
                color: "#fff",
                cursor: "pointer",
                marginRight: "10px",
                transition: "background-color 0.3s ease",
                ":hover": {
                  backgroundColor: "#3a6a73",
                },
              }}
            >
              ◀️ Prev
            </button>
            <span style={{ fontSize: "16px", fontWeight: "500" }}>
              Page {currentPage}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentGifts.length < giftsPerPage}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#50868f",
                color: "#fff",
                cursor: "pointer",
                marginLeft: "10px",
                transition: "background-color 0.3s ease",
                ":hover": {
                  backgroundColor: "#3a6a73",
                },
              }}
            >
              Next ▶️
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default GiftPage;
